import {
  CsvFormat,
  RedactedInvoiceResponse,
  SortDirection,
  InvoiceLineItemState,
  UUID,
} from '@mesa-labs/mesa-api/dist/types';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type InvoiceTimeRange = number; // in days;

export const PaginationLimits = [
  { key: '10', value: 10 },
  { key: '25', value: 25 },
  { key: '50', value: 50 },
  { key: '100', value: 100 },
];

export type ExportFormatType = 'operator' | 'vendor';

// netAmount and grossAmount are what the row added to the selection totals, so deselecting subtracts exactly that
export type SelectiveGetPaidNowRowMetadata = {
  externalClientId: string;
  externalVendorId: string;
  netAmount: number;
  grossAmount: number;
};

export type InvoicesState = {
  invoices: RedactedInvoiceResponse[];
  page: number;
  limit: { key: string; value: number };
  total?: number;
  searchTerm?: string;
  sortField?: string;
  sortDirection: SortDirection;
  exportFormat: CsvFormat;
  // used to determine operator vs. vendor "exportFormat"
  exportFormatType: ExportFormatType;
  excludeStates: InvoiceLineItemState[];
  instantPayoutId?: string;
  mesaOnDemandId?: string;
  selectiveGetPaidNowRows: UUID[];
  selectiveGetPaidNowRowsMetadata: Record<string, SelectiveGetPaidNowRowMetadata>;
  selectiveGetPaidNowTotal: number;
  selectiveGetPaidNowGrossTotal: number;
  payOnApprovalByRequestId?: string;
};

const initialState: InvoicesState = {
  invoices: [],
  page: 1,
  limit: PaginationLimits[0],
  total: undefined,
  searchTerm: undefined,
  sortField: 'eligibleAt',
  sortDirection: SortDirection.DESCENDING,
  exportFormat: CsvFormat.MESA_CSV_FORMAT,
  exportFormatType: 'vendor',
  excludeStates: [],
  instantPayoutId: undefined,
  mesaOnDemandId: undefined,
  selectiveGetPaidNowRows: [],
  selectiveGetPaidNowRowsMetadata: {},
  selectiveGetPaidNowTotal: 0,
  selectiveGetPaidNowGrossTotal: 0,
  payOnApprovalByRequestId: undefined,
};

const invoicesSlice = createSlice({
  name: 'invoices',
  initialState,
  reducers: {
    updateInvoices(state: InvoicesState, action: PayloadAction<RedactedInvoiceResponse[]>) {
      state.invoices = action.payload;
    },
    updatePage(state: InvoicesState, action: PayloadAction<number>) {
      state.page = action.payload;
    },
    updateLimit(state: InvoicesState, action: PayloadAction<{ key: string; value: number }>) {
      state.limit = action.payload;
    },
    updateTotal(state: InvoicesState, action: PayloadAction<number>) {
      state.total = action.payload;
    },
    updateSearchTerm(state: InvoicesState, action: PayloadAction<string>) {
      state.searchTerm = action.payload;
    },
    updateSortField(state: InvoicesState, action: PayloadAction<string>) {
      state.sortField = action.payload;
    },
    updateSortDirection(state: InvoicesState, action: PayloadAction<SortDirection>) {
      state.sortDirection = action.payload;
    },
    updateExportFormat(state: InvoicesState, action: PayloadAction<CsvFormat>) {
      state.exportFormat = action.payload;
    },
    updateExportFormatType(state: InvoicesState, action: PayloadAction<ExportFormatType>) {
      state.exportFormatType = action.payload;
    },
    updateExcludeStates(state: InvoicesState, action: PayloadAction<InvoiceLineItemState[]>) {
      state.excludeStates = action.payload;
    },
    updateInstantPayoutId(state: InvoicesState, action: PayloadAction<string>) {
      state.instantPayoutId = action.payload;
    },
    updateMesaOnDemandId(state: InvoicesState, action: PayloadAction<string>) {
      state.mesaOnDemandId = action.payload;
    },
    updateSelectiveGetPaidNowRows(state: InvoicesState, action: PayloadAction<UUID[]>) {
      state.selectiveGetPaidNowRows = action.payload;
    },
    updateSelectiveGetPaidNowRowsMetadata(state: InvoicesState, action: PayloadAction<Record<string, SelectiveGetPaidNowRowMetadata>>) {
      state.selectiveGetPaidNowRowsMetadata = action.payload;
    },
    updateSelectiveGetPaidNowTotal(state: InvoicesState, action: PayloadAction<number>) {
      state.selectiveGetPaidNowTotal = action.payload;
    },
    updateSelectiveGetPaidNowGrossTotal(state: InvoicesState, action: PayloadAction<number>) {
      state.selectiveGetPaidNowGrossTotal = action.payload;
    },
    clearSelectiveGetPaidNowSelection(state: InvoicesState) {
      state.selectiveGetPaidNowRows = [];
      state.selectiveGetPaidNowRowsMetadata = {};
      state.selectiveGetPaidNowTotal = 0;
      state.selectiveGetPaidNowGrossTotal = 0;
    },
    updatePayOnApprovalByRequestId(state: InvoicesState, action: PayloadAction<string>) {
      state.payOnApprovalByRequestId = action.payload;
    },
  },
});

export const {
  updateInvoices,
  updatePage,
  updateLimit,
  updateTotal,
  updateSearchTerm,
  updateSortField,
  updateSortDirection,
  updateExportFormat,
  updateExportFormatType,
  updateExcludeStates,
  updateInstantPayoutId,
  updateMesaOnDemandId,
  updateSelectiveGetPaidNowRows,
  updateSelectiveGetPaidNowRowsMetadata,
  updateSelectiveGetPaidNowTotal,
  updateSelectiveGetPaidNowGrossTotal,
  clearSelectiveGetPaidNowSelection,
  updatePayOnApprovalByRequestId,
} = invoicesSlice.actions;
export default invoicesSlice.reducer;
